import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpCircle, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Create,
  Datagrid,
  DateField,
  Edit,
  List,
  SimpleForm,
  TextField,
  TextInput,
  required,
  useNotify,
  useRecordContext,
} from "react-admin";
import { EditorToolbar } from "../../components";
import { authClient } from "../../server";
import { bulkEmailResources } from "./common";

const emptySending = {
  name: "",
  from_address: "TorahAnytime <info@torahanytime.com>",
  reply_to_address: "info@torahanytime.com",
  audience_mode: "snapshot",
  overlap_mode: "first",
};
const defaultSample = '{"email":"preview@example.com"}';

function HelpLabel({ children, help }) {
  return (
    <Box component="span">
      {children}
      <Tooltip arrow enterTouchDelay={0} leaveTouchDelay={5000} title={help}>
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={`Help for ${children}`}
          onClick={(event) => {
            // Focusing opens the tooltip on click while MUI handles hover and touch interactions.
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.focus();
          }}
          sx={{
            display: "inline-flex",
            ml: 0.5,
            cursor: "help",
            pointerEvents: "auto",
            verticalAlign: "middle",
          }}
        >
          <HelpCircle size={14} aria-hidden="true" />
        </Box>
      </Tooltip>
    </Box>
  );
}

function CampaignList() {
  return (
    <List sort={{ field: "id", order: "DESC" }} perPage={50}>
      <Datagrid rowClick="edit">
        <TextField source="id" />
        <TextField source="name" />
        <TextField source="description" />
        <DateField source="created_at" showTime />
      </Datagrid>
    </List>
  );
}

function CampaignFields() {
  return (
    <>
      <TextInput
        source="name"
        fullWidth
        validate={required()}
        helperText="This name is shown to recipients when they manage their email preferences. Use a clear, long-lasting category name, such as “Annual Fundraiser.” Create separate Sendings for individual emails or yearly editions, such as “Fundraiser 2026” and “Fundraiser 2027.”"
      />
      <TextInput source="description" fullWidth multiline minRows={3} />
    </>
  );
}

function CampaignCreate() {
  return (
    <Create redirect="edit" mutationMode="pessimistic">
      <SimpleForm>
        <CampaignFields />
      </SimpleForm>
    </Create>
  );
}

// Sendings stay nested here because their API ownership and lifecycle belong to one campaign.
function SendingBuilder() {
  const campaign = useRecordContext();
  const notify = useNotify();
  const [sendings, setSendings] = useState([]);
  const [sending, setSending] = useState();
  const [sendingForm, setSendingForm] = useState(emptySending);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignment, setAssignment] = useState({
    segment_id: "",
    template_id: "",
    position: 1,
  });
  const [sample, setSample] = useState(defaultSample);
  const [sampleAssignmentId, setSampleAssignmentId] = useState("");
  const [preview, setPreview] = useState();
  const [testEmails, setTestEmails] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");

  const loadSendings = async () => {
    const response = await authClient.get(
      `/admin/bulk-email/campaigns/${campaign.id}/sendings?_start=0&_end=200`,
    );
    setSendings(response.json);
  };

  const loadSending = async (selectedSending) => {
    const response = await authClient.get(
      `/admin/bulk-email/campaigns/${campaign.id}/sendings/${selectedSending.id}`,
    );
    setSending(response.json);
    setSendingForm(response.json);
    setSampleAssignmentId(String(response.json.assignments[0]?.id || ""));
    // Preview data belongs to one Sending and assignment, so never carry it across contexts.
    setSample(defaultSample);
    setPreview(undefined);
  };

  useEffect(() => {
    Promise.all([
      authClient.get(
        `/admin/bulk-email/campaigns/${campaign.id}/sendings?_start=0&_end=200`,
      ),
      authClient.get("/admin/bulk-email/segments?_start=0&_end=500"),
      authClient.get("/admin/bulk-email/templates?_start=0&_end=500"),
    ])
      .then(([sendingResponse, segmentResponse, templateResponse]) => {
        setSendings(sendingResponse.json);
        setSegments(segmentResponse.json);
        setTemplates(templateResponse.json);
      })
      .catch((error) =>
        notify(error.body?.message || error.message, { type: "error" }),
      );
  }, [campaign.id, notify]);

  const saveSending = async () => {
    try {
      const response = sending
        ? await authClient.put(
            `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}`,
            sendingForm,
          )
        : await authClient.post(
            `/admin/bulk-email/campaigns/${campaign.id}/sendings`,
            sendingForm,
          );
      await loadSendings();
      await loadSending(response.json);
      notify("Sending saved", { type: "success" });
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const addAssignment = async () => {
    try {
      await authClient.post(
        `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/assignments`,
        {
          ...assignment,
          segment_id: Number(assignment.segment_id),
          template_id: Number(assignment.template_id),
        },
      );
      await loadSending(sending);
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const removeAssignment = async (id) => {
    await authClient.delete(
      `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/assignments/${id}`,
    );
    await loadSending(sending);
  };

  const moveAssignment = async (row, direction) => {
    const index = sending.assignments.findIndex((item) => item.id === row.id);
    const other = sending.assignments[index + direction];
    if (!other) return;
    await authClient.put(
      `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/assignments/${row.id}`,
      { position: other.position },
    );
    await loadSending(sending);
  };

  const validateSending = async () => {
    await authClient.post(
      `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/validations`,
      {},
    );
    notify("Validation started", { type: "info" });
    window.setTimeout(() => loadSending(sending), 1500);
  };

  const renderPreview = async () => {
    try {
      const response = await authClient.post(
        `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/preview`,
        {
          recipient: JSON.parse(sample),
          assignmentId: Number(
            sampleAssignmentId || sending.assignments[0]?.id,
          ),
        },
      );
      setPreview(response.json);
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const sendTest = async () => {
    try {
      await authClient.post(
        `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/tests`,
        {
          recipient: JSON.parse(sample),
          assignmentId: Number(
            sampleAssignmentId || sending.assignments[0]?.id,
          ),
          testEmails: testEmails
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean),
        },
      );
      notify("Test send started", { type: "success" });
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const loadRandomRecipient = () => {
    const validation = sending.latest_validation;
    if (
      validation?.status !== "success" ||
      validation.definition_hash !== sending.current_definition_hash
    ) {
      notify("Validate this Sending before loading Segment preview data", {
        type: "warning",
      });
      return;
    }
    const assignmentId = Number(
      sampleAssignmentId || sending.assignments[0]?.id,
    );
    const assignment = validation.summary?.assignments?.find(
      (row) => Number(row.assignment_id) === assignmentId,
    );
    const rows = assignment?.sample_rows || [];
    if (!rows.length) {
      notify("The validation did not find a recipient for this Segment", {
        type: "warning",
      });
      return;
    }
    // These validation samples supply template data only; sendTest uses explicit testEmails.
    setSample(
      JSON.stringify(rows[Math.floor(Math.random() * rows.length)], null, 2),
    );
    setPreview(undefined);
    notify("Loaded a random recipient from the Segment for preview data", {
      type: "success",
    });
  };

  const launch = async () => {
    const validation = sending.latest_validation;
    if (
      !window.confirm(
        `Send to ${
          validation?.summary?.effective_recipients || 0
        } effective recipients?`,
      )
    ) {
      return;
    }
    let override;
    if (
      (validation?.warnings || []).some(
        (warning) => warning.code === "missing_unsubscribe_link",
      )
    ) {
      override = window.prompt(
        "Explain why this email can be sent without an email-preferences link using {{ system.email_preferences_url }}:",
      );
      if (!override) return;
    }
    try {
      await authClient.post(
        `/admin/bulk-email/campaigns/${campaign.id}/sendings/${sending.id}/runs`,
        {
          confirmed_validation_id: validation.id,
          confirmed_definition_hash: sending.current_definition_hash,
          confirmed_counts: true,
          scheduled_for: scheduledFor
            ? new Date(scheduledFor).toISOString()
            : null,
          unsubscribe_override_reason: override,
        },
      );
      notify(scheduledFor ? "Sending scheduled" : "Sending launched", {
        type: "success",
      });
      await loadSending(sending);
      await loadSendings();
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Sendings
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {sendings.map((row) => (
          <Button
            type="button"
            key={row.id}
            variant={sending?.id === row.id ? "contained" : "outlined"}
            onClick={() => loadSending(row)}
          >
            {row.name} ({row.state})
          </Button>
        ))}
        <Button
          type="button"
          onClick={() => {
            setSending(undefined);
            setSendingForm(emptySending);
            setSampleAssignmentId("");
            setSample(defaultSample);
            setPreview(undefined);
          }}
        >
          New sending
        </Button>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(180px, 1fr))",
              gap: 2,
            }}
          >
            <FormControl>
              <InputLabel
                htmlFor="bulk-email-sending-name"
                variant="outlined"
                // Pin the variant so MUI applies the outlined field's raised-label transform on focus.
                sx={{ pointerEvents: "auto" }}
              >
                <HelpLabel help="An internal name for this individual sending within the campaign. Recipients do not see it.">
                  Sending name
                </HelpLabel>
              </InputLabel>
              <OutlinedInput
                id="bulk-email-sending-name"
                label={"Sending name\u00a0\u00a0\u00a0"}
                value={sendingForm.name}
                onChange={(event) =>
                  setSendingForm({ ...sendingForm, name: event.target.value })
                }
              />
            </FormControl>
            <MuiTextField
              label="From"
              value={sendingForm.from_address}
              onChange={(event) =>
                setSendingForm({
                  ...sendingForm,
                  from_address: event.target.value,
                })
              }
            />
            <MuiTextField
              label="Reply-To"
              value={sendingForm.reply_to_address}
              onChange={(event) =>
                setSendingForm({
                  ...sendingForm,
                  reply_to_address: event.target.value,
                })
              }
            />
            <FormControl>
              <InputLabel sx={{ pointerEvents: "auto" }}>
                <HelpLabel help="Controls when recipients are resolved. Snapshot saves the audience when the run is launched or scheduled; Live evaluates segments when dispatch begins.">
                  Audience
                </HelpLabel>
              </InputLabel>
              <Select
                label={"Audience\u00a0\u00a0\u00a0"}
                value={sendingForm.audience_mode}
                onChange={(event) =>
                  setSendingForm({
                    ...sendingForm,
                    audience_mode: event.target.value,
                  })
                }
              >
                <MenuItem value="snapshot">Snapshot now</MenuItem>
                <MenuItem value="live">Resolve at dispatch</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel sx={{ pointerEvents: "auto" }}>
                <HelpLabel help="Controls what happens when the same email matches multiple assignments: first match wins, block the sending, or send every match. Suppressions are applied first.">
                  Overlap
                </HelpLabel>
              </InputLabel>
              <Select
                label={"Overlap\u00a0\u00a0\u00a0"}
                value={sendingForm.overlap_mode}
                onChange={(event) =>
                  setSendingForm({
                    ...sendingForm,
                    overlap_mode: event.target.value,
                  })
                }
              >
                <MenuItem value="first">First match wins</MenuItem>
                <MenuItem value="block">Block on overlap</MenuItem>
                <MenuItem value="all">Send every match</MenuItem>
              </Select>
            </FormControl>
            <Button type="button" variant="contained" onClick={saveSending}>
              Save sending
            </Button>
          </Box>
        </CardContent>
      </Card>

      {sending && (
        <>
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6">Ordered assignments</Typography>
              {sending.assignments.map((row) => (
                <Box
                  key={row.id}
                  sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
                >
                  <Typography sx={{ flexGrow: 1 }}>
                    {row.position}. {row.segment?.name} → {row.template?.name}
                  </Typography>
                  <Button type="button" onClick={() => moveAssignment(row, -1)}>
                    Up
                  </Button>
                  <Button type="button" onClick={() => moveAssignment(row, 1)}>
                    Down
                  </Button>
                  <Button
                    type="button"
                    color="error"
                    onClick={() => removeAssignment(row.id)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Segment</InputLabel>
                  <Select
                    label="Segment"
                    value={assignment.segment_id}
                    onChange={(event) =>
                      setAssignment({
                        ...assignment,
                        segment_id: event.target.value,
                      })
                    }
                  >
                    {segments.map((row) => (
                      <MenuItem key={row.id} value={row.id}>
                        {row.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Template</InputLabel>
                  <Select
                    label="Template"
                    value={assignment.template_id}
                    onChange={(event) =>
                      setAssignment({
                        ...assignment,
                        template_id: event.target.value,
                      })
                    }
                  >
                    {templates.map((row) => (
                      <MenuItem key={row.id} value={row.id}>
                        {row.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MuiTextField
                  type="number"
                  label="Position"
                  value={assignment.position}
                  onChange={(event) =>
                    setAssignment({
                      ...assignment,
                      position: Number(event.target.value),
                    })
                  }
                />
                <Button type="button" onClick={addAssignment}>
                  Add assignment
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={validateSending}
                >
                  Validate
                </Button>
                <Button type="button" onClick={() => loadSending(sending)}>
                  Refresh validation
                </Button>
              </Box>
              {sending.latest_validation && (
                <Alert
                  severity={
                    sending.validation_is_current ? "success" : "warning"
                  }
                >
                  Validation {sending.latest_validation.status}.{" "}
                  {sending.validation_is_current
                    ? "Current"
                    : "Stale—validate again."}
                </Alert>
              )}
              {sending.latest_validation?.summary?.assignments?.map((row) => (
                <Typography key={row.assignment_id} sx={{ mt: 1 }}>
                  Assignment {row.assignment_id}: raw {row.raw}, suppressed{" "}
                  {row.suppressed}, overlapping {row.overlapping}, effective{" "}
                  {row.effective}; columns: {row.columns.join(", ")}
                </Typography>
              ))}
              {(sending.latest_validation?.warnings || []).map(
                (warning, index) => (
                  <Alert key={index} severity="warning">
                    {warning.message || warning.code} (template{" "}
                    {warning.template_id})
                  </Alert>
                ),
              )}
              {(sending.latest_validation?.errors || []).map((error, index) => (
                <Alert key={index} severity="error">
                  {error.code}: {error.message || error.count}
                </Alert>
              ))}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <InputLabel>Sample Segment</InputLabel>
                    <Select
                      label="Sample Segment"
                      value={sampleAssignmentId}
                      onChange={(event) => {
                        setSampleAssignmentId(event.target.value);
                        setSample(defaultSample);
                        setPreview(undefined);
                      }}
                    >
                      {sending.assignments.map((row) => (
                        <MenuItem key={row.id} value={String(row.id)}>
                          {row.segment?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button type="button" onClick={loadRandomRecipient}>
                    Use random Segment recipient
                  </Button>
                  <MuiTextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Preview recipient JSON"
                    value={sample}
                    onChange={(event) => setSample(event.target.value)}
                  />
                  <Typography variant="caption" display="block">
                    This only supplies template data. Test delivery still goes
                    only to the addresses entered below.
                  </Typography>
                  <Button type="button" onClick={renderPreview}>
                    Render preview
                  </Button>
                  <MuiTextField
                    fullWidth
                    label="Test addresses (comma-separated)"
                    value={testEmails}
                    onChange={(event) => setTestEmails(event.target.value)}
                  />
                  <Button type="button" onClick={sendTest}>
                    Send test
                  </Button>
                </Box>
                <iframe
                  title="Bulk email preview"
                  sandbox=""
                  srcDoc={preview?.html || ""}
                  style={{ width: "100%", minHeight: 400 }}
                />
              </Box>
              <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                <MuiTextField
                  type="datetime-local"
                  label="Schedule (browser timezone)"
                  value={scheduledFor}
                  onChange={(event) => setScheduledFor(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  type="button"
                  variant="contained"
                  color="success"
                  disabled={
                    !sending.validation_is_current || sending.has_active_run
                  }
                  onClick={launch}
                >
                  {scheduledFor ? "Schedule" : "Send now"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

function CampaignEdit() {
  return (
    <Edit mutationMode="pessimistic">
      <SimpleForm toolbar={<EditorToolbar showDelete />}>
        <CampaignFields />
        <SendingBuilder />
      </SimpleForm>
    </Edit>
  );
}

export const BulkEmailCampaigns = {
  category: "email",
  name: bulkEmailResources.campaigns,
  list: CampaignList,
  create: CampaignCreate,
  edit: CampaignEdit,
  icon: Mail,
  roles: ["administrator"],
  options: { label: "Email Campaigns" },
};
